import { TITLE_LENGTH_LIMIT } from 'src/app/shared/constants/form-validators';
import { WorkflowV1Read, WorkflowV1Run } from '.';
import { isOldSingleFileUpload, isOldWorkflowStarter } from '../../../utils/utils';
import { SegmentationType } from '../enums';
import { StepType } from '../enums/step-type.enum';
import { WorkflowType } from '../enums/workflow-type.enum';
import { WorkflowVisibility } from '../types/workflow-visibility.type';
import { connectTo } from '../utils/workflow-step-utils';
import { ModuleV1Read } from './module-v1-read.model';
import { ModuleVersion } from './module-version.model';
import {
    FileStepParameter,
    IVersionReadModel,
    ListFileStepParameter,
    ModuleParameter,
    StepOutputParameter,
    StepParameter,
    StringStepParameter,
} from './parameter.model';
import { StepInput } from './run-step.model';
import { Step } from './step.model';
import { WorkflowInput, WorkflowNode, WorkflowSpecificationModel } from './workflow-specification';

export interface IWorkflowModel {
    id: string;
    title: string;
    producerId?: string;
    description?: string;
    summary?: string;
    usedModuleVersions: IVersionReadModel[];
    usedModules: ModuleV1Read[];
    visibility: WorkflowVisibility;
    type: WorkflowType;
    segmentationTypeSupported?: SegmentationType;
    isDefaultForType?: boolean;
    isFeatured?: boolean;
    mlVersion?: number;
    successorWorkflowIds?: string[];
}

export class Workflow {
    private _steps: Step[] = [];
    private _isCopy: boolean = false;

    constructor(private workflowModel: IWorkflowModel) {}

    get steps(): Step[] {
        return this._steps;
    }

    get title(): string {
        return this.workflowModel.title;
    }

    set title(title: string) {
        this.workflowModel.title = title;
    }

    get id(): string {
        return this.workflowModel.id;
    }

    get description(): string | undefined {
        return this.workflowModel.description;
    }

    get summary(): string | undefined {
        return this.workflowModel.summary;
    }

    get producerId(): string | undefined {
        return this.workflowModel.producerId;
    }

    get type(): WorkflowType {
        return this.workflowModel.type;
    }

    get segmentationTypeSupported(): SegmentationType | undefined {
        return this.workflowModel.segmentationTypeSupported;
    }

    get isDefaultForType(): boolean | undefined {
        return this.workflowModel.isDefaultForType;
    }

    get visibility(): WorkflowVisibility {
        return this.workflowModel.visibility;
    }

    get isCopy(): boolean {
        return this._isCopy;
    }

    /**
     * Indicates if the workflow has any deprecated modules.
     * This can be:
     *  - Single File Upload (old ModuleDefineInput)
     *  - Old WorkflowDefineInput
     */
    get hasDeprecatedModules(): boolean {
        return this.steps.some((step: Step, index: number) => isOldSingleFileUpload(step.title!, index) || isOldWorkflowStarter(step.title!, index));
    }

    /**
     * Checks if the given steps can be connected, and connects them
     *
     * @param indexOrFlowId index (number) or flowId (string) of the  SOURCE step
     * @param indexOrFlowId2 index (number) or flowId (string) of the TARGET step
     */
    connectStepsIfPossible(indexOrFlowId: number | string, indexOrFlowId2: number | string): boolean {
        let stepHasBeenConnected = false;
        let sourceStep: Step | undefined;
        let targetStep: Step | undefined;

        // Find source step
        if (Number.isInteger(indexOrFlowId)) {
            sourceStep = this.steps[indexOrFlowId];
        } else {
            sourceStep = this.getStep(indexOrFlowId as string);
        }

        // find target step
        if (Number.isInteger(indexOrFlowId2)) {
            targetStep = this.steps[indexOrFlowId2];
        } else {
            targetStep = this.getStep(indexOrFlowId2 as string);
        }

        if (sourceStep != null && targetStep != null) {
            for (const sourceOutputParam of sourceStep.moduleVersion.outputParameters) {
                for (const targetInputParam of targetStep.moduleVersion.inputParameters) {
                    if (this.isValidConnectionBetweenParameters(sourceOutputParam.key, sourceStep, targetInputParam.key, targetStep) && !stepHasBeenConnected) {
                        stepHasBeenConnected = connectTo(sourceOutputParam, targetInputParam.key, targetStep, sourceStep);
                    }
                }
            }
        }

        return stepHasBeenConnected;
    }

    /**
     * StepByStep run is allowed, when
     *      1. the WorkflowDefineInput step is connected to the file_list parameter (SingleRun)
     *      2. the WorkflowDefineInput step is connected to the file parameter, with only ONE file (SingleRun)
     *      3. the OldWorkflowStarter step has only one file (SingleRun for old workflows)
     */
    isStepByStepRunAllowedForWorkflow(): boolean {
        let isStepByStepRunAllowed: boolean = true;
        const defineInputStep: Step = this.steps[0];

        if (
            defineInputStep != null &&
            (defineInputStep.type === StepType.WorkflowDefineInput || defineInputStep.type === StepType.OldWorkflowStarter) &&
            defineInputStep.inputParameters &&
            defineInputStep.inputParameters.length > 0
        ) {
            if (defineInputStep.type === StepType.WorkflowDefineInput) {
                //  Case 2:
                if (this.isFileParameterSetInWorkflowDefineInput() === true && defineInputStep.getNumberOfStepDefinedInputFiles() > 1) {
                    isStepByStepRunAllowed = false;
                } else {
                    // Case 1:
                    isStepByStepRunAllowed = true;
                }
            } else if (defineInputStep.type === StepType.OldWorkflowStarter) {
                // Case 3:
                isStepByStepRunAllowed = defineInputStep.getNumberOfStepDefinedInputFiles() <= 1;
            }
        }

        return isStepByStepRunAllowed;
    }

    resetSystemModuleInputs(): void {
        if (this.steps) {
            this.steps.forEach(step => {
                if (step.type === StepType.WorkflowDefineInput || step.type === StepType.ModuleDefineInput || step.type === StepType.OldWorkflowStarter) {
                    step.inputParameters = [];
                }
            });
        }
    }

    private set usedModuleVersions(usedModuleVersions: IVersionReadModel[]) {
        this.workflowModel.usedModuleVersions = usedModuleVersions;
    }

    private set usedModules(usedModules: ModuleV1Read[]) {
        this.workflowModel.usedModules = usedModules;
    }

    getStepIndex(stepOrFlowId: Step | string): number {
        let flowId: string = '';
        if (typeof stepOrFlowId === 'string') {
            flowId = stepOrFlowId;
        } else if (stepOrFlowId instanceof Step) {
            flowId = stepOrFlowId.flowId;
        }

        return this.steps.findIndex((s: Step) => s.flowId === flowId);
    }

    areAllStepsValid(myUserId?: string): boolean {
        return this._steps.every((step: Step) => step.isValid(myUserId));
    }

    isValid(myUserId?: string): boolean {
        const isWorkflowValid = this.isWorkflowTitleValid(this.workflowModel.title);
        if (isWorkflowValid) {
            return this.areAllStepsValid(myUserId);
        }

        return isWorkflowValid;
    }

    isWorkflowTitleValid(title: string): boolean {
        title = title.trim();
        return title != null && title !== '' && title.length < TITLE_LENGTH_LIMIT;
    }

    getSpecification(): WorkflowSpecificationModel {
        return this.generateNewSpecification(this);
    }

    getReadModelV1(): WorkflowV1Read {
        return {
            id: this.id,
            title: this.title,
            description: this.description,
            summary: this.summary,
            specification: this.getSpecification(),
            producerId: this.producerId,
            usedModuleVersions: this.workflowModel.usedModuleVersions,
            usedModules: this.workflowModel.usedModules,
            type: this.type,
            segmentationTypeSupported: this.segmentationTypeSupported,
            isDefaultForType: this.isDefaultForType,
            isFeatured: this.workflowModel.isFeatured,
            mlVersion: this.workflowModel.mlVersion,
            visibility: this.visibility,
        } as WorkflowV1Read;
    }

    getRunModelV1(): WorkflowV1Run {
        return {
            id: this.id,
            title: this.title,
            description: this.description,
            summary: this.summary,
            specification: this.getSpecification(),
            producerId: this.producerId,
            usedModuleVersions: this.workflowModel.usedModuleVersions,
            usedModules: this.workflowModel.usedModules,
            segmentationTypeSupported: this.segmentationTypeSupported,
            type: this.type,
            isDefaultForType: this.isDefaultForType,
            isFeatured: this.workflowModel.isFeatured,
            mlVersion: this.workflowModel.mlVersion,
            visibility: this.visibility,
        } as WorkflowV1Run;
    }

    getStep(flowId: string): Step | undefined {
        return this.steps.find((step: Step) => step.flowId === flowId);
    }

    /**
     * Adds a {step} at the given {index}
     */
    addStep(step: Step, index: number): boolean {
        // Initialize module correctly
        // Safety check to not forget to correctly initialize the model
        if (step.moduleVersion.module != null) {
            step.moduleVersion.module = new ModuleV1Read(step.moduleVersion.module);
        }
        if (this._steps.every(s => s.flowId !== step.flowId)) {
            if (index === this._steps.length + 1) {
                this._steps.push(step);
                this.addUsedModuleAndModuleVersion(step);
                return true;
            } else if (index >= 0 && index <= this._steps.length) {
                this.addUsedModuleAndModuleVersion(step);
                this._steps.splice(index, 0, step);
                return true;
            }
        }
        return false;
    }

    /**
     * Appends a step at last index (simple push)
     */
    appendStep(step: Step): boolean {
        return this.addStep(step, this._steps.length + 1);
    }

    removeStep(index: number): boolean {
        const stepToRemove = this._steps[index];
        if (stepToRemove != null) {
            this._steps.splice(index, 1);
            this._steps.forEach(s => s.onOtherStepRemoved(stepToRemove));
            return true;
        }
        return false;
    }

    canEdit(userId: string | undefined): boolean {
        return userId != null && this.workflowModel.producerId === userId;
    }

    markWorkflowTitleAsCopy(): void {
        if (!this._isCopy) {
            this.workflowModel.title = `${this.workflowModel.title} (copy)`;
            this._isCopy = true;
        }
    }

    changeModuleVersionForStep(thisStep: Step, moduleVersion: ModuleVersion): void {
        thisStep.updateModuleVersion(moduleVersion);
        this.updateModuleVersionsInWorkflow();
        this.updateUsedModulesInWorkflow();

        // iterate through steps (except for the current step) and check if all stepOutputparameters still exist on this step
        this.steps.forEach((step: Step) => {
            if (thisStep.flowId !== step.flowId) {
                step.validateStepOutputParameters(thisStep);
            }
        });
    }

    private isValidConnectionBetweenParameters(sourceOutputParamKey: string, sourceStep: Step, targetInputParamKey: string, targetStep: Step): boolean {
        let isValidConnection = false;
        const sourceModuleOutputParam: ModuleParameter | undefined = sourceStep.moduleVersion.getOutputParameter(sourceOutputParamKey);
        const targetModuleInputParam: ModuleParameter | undefined = targetStep.moduleVersion.getInputParameter(targetInputParamKey);

        const targetStepIndex: number = this.getStepIndex(targetStep.flowId);
        const sourceStepIndex: number = this.getStepIndex(sourceStep.flowId);

        /**
         * Validation rules:
         * 1. Type must be the same (i.e. FileList)
         * 2. Steps must not be disabled
         * 3. FlowId of source and target must not be the same
         * 4. outputs can only be connected to inputs which are placed after them
         * 5. Input parameter cannot have a connection yet
         * -----------------
         *
         * Missing validations:
         * 6. Connection does not exist yet between given steps
         * 7. Step (parameter) exceeds max connections (we only have that value in Editor Visualizer)
         */
        if (
            sourceModuleOutputParam != null &&
            targetModuleInputParam != null &&
            sourceModuleOutputParam.type === targetModuleInputParam.type &&
            !sourceStep.disabled &&
            !targetStep.disabled &&
            sourceStep.flowId !== targetStep.flowId &&
            targetStepIndex > sourceStepIndex &&
            !targetStep.hasParameterConnection(targetInputParamKey)
        ) {
            isValidConnection = true;
        }

        return isValidConnection;
    }

    private addUsedModuleAndModuleVersion(step: Step) {
        if (this.workflowModel.usedModuleVersions) {
            const foundModuleVersion: IVersionReadModel | undefined = this.workflowModel.usedModuleVersions.find(mv => mv.id === step.moduleVersion.id);
            if (!foundModuleVersion) {
                this.workflowModel.usedModuleVersions.push(step.moduleVersion.versionReadModel);
            }
        }
        if (this.workflowModel.usedModules) {
            const foundModule: ModuleV1Read | undefined = this.workflowModel.usedModules.find(m => m.id === (step.moduleVersion.module as ModuleV1Read).id);
            if (!foundModule) {
                this.workflowModel.usedModules.push(step.moduleVersion.module as ModuleV1Read);
            }
        }
    }

    /**
     * Maps all ModuleParameters to the structure of the WorkflowSpecification
     * Format:
     *  - Static Parameter
     *      {key}: {value}
     *
     *  - StepOutputParameter
     *      {key}: {
     *              operation_id: stepParam.sourceFlowId
     *              output: stepParam.sourceOutputKey
     *           }
     */
    private mapParamsToKeyValuePair(moduleParameters: StepParameter[]): StepInput {
        const paramObject: StepInput = {};
        moduleParameters.forEach((param: StepParameter) => {
            if (param instanceof StepOutputParameter) {
                const workflowInput: WorkflowInput = {
                    operation_id: param.sourceFlowId,
                    output: param.sourceOutputKey,
                };
                paramObject[param.key] = workflowInput;
            } else {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const paramValue: any = (param as any).value;
                // we need to check if paramValue is undefined and if it is we need to convert it to null since  otherwise
                // appropriate parameter key will not be send to the backend (service will omit undefined parameter)
                // this can cause later issues when comparing workflow objects for tracking changes on it
                paramObject[param.key] = paramValue === undefined ? null : paramValue;
            }
        });
        return paramObject;
    }

    private generateNewSpecification(workflow: Workflow): WorkflowSpecificationModel {
        const result = new WorkflowSpecificationModel();

        result.workflow!.flow = workflow.steps.map((step: Step) => {
            const node: WorkflowNode = {
                id: step.flowId,
                run: {
                    module: step.moduleVersion.id,
                    input: this.mapParamsToKeyValuePair(step.inputParameters),
                },
            };
            return node;
        });
        result.workflow!.id = workflow.id;

        return result;
    }

    private updateModuleVersionsInWorkflow(): void {
        const usedModuleVersions: IVersionReadModel[] = [];
        this.steps.forEach((step: Step) => {
            const moduleVersionToAdd: IVersionReadModel = step.moduleVersion.versionReadModel;

            // only add if moduleVersion doesn't exist yet
            if (usedModuleVersions.find((mv: IVersionReadModel) => mv.id === moduleVersionToAdd.id) == null) {
                usedModuleVersions.push(step.moduleVersion.versionReadModel);
            }
        });

        this.usedModuleVersions = usedModuleVersions;
    }

    private updateUsedModulesInWorkflow(): void {
        const usedModules: ModuleV1Read[] = [];
        this.steps.forEach((step: Step) => {
            const moduleToAdd: ModuleV1Read = step.moduleVersion.module as ModuleV1Read;

            // only add if module doesn't exist yet
            if (usedModules.find((m: ModuleV1Read) => m.id === moduleToAdd.id) == null) {
                usedModules.push(moduleToAdd);
            }
        });

        this.usedModules = usedModules;
    }

    /**
     * Indicates if the WorkflowDefineInput has its files in file parameter.
     * Checks in which parameter (file or file_list) the files are in. If no files are
     * defined, checks to which parameter the module is connected to.
     */
    private isFileParameterSetInWorkflowDefineInput(): boolean {
        let isFileParamSet = false;
        const isNewDefineInputStep: boolean = this.steps.length > 0 && this.steps[0].type === StepType.WorkflowDefineInput;
        if (isNewDefineInputStep === true) {
            const defineInputStep: Step = this.steps[0];
            const fileParam: StepParameter | undefined = defineInputStep.inputParameters.find(
                (param: StepParameter) => param instanceof FileStepParameter || param instanceof StringStepParameter,
            );
            const fileListParam: StepParameter | undefined = defineInputStep.inputParameters.find(
                (param: StepParameter) => param instanceof ListFileStepParameter,
            );
            if (defineInputStep.inputParameters.length > 0 && fileParam != null && fileParam.getValue() != null && fileParam.getValue() !== '') {
                // Check if one or more files can be found in file parameter
                isFileParamSet = true;
            } else if (defineInputStep.inputParameters.length > 0 && fileListParam != null && fileListParam.getValue() && fileListParam.getValue().length > 0) {
                // Check if one or more files can be found in file_list parameter (then file param is NOT set)
                isFileParamSet = false;
            } else {
                // If neither parameter is set, check if the fileParam has a connection
                isFileParamSet = this.isWorkflowDefineInputConnectedToParam('file');
            }
        }

        return isFileParamSet;
    }

    private isWorkflowDefineInputConnectedToParam(paramKey: 'file' | 'file_list'): boolean {
        let isConnectedToFileParam: boolean = false;
        const defineInputStep: Step = this.steps[0];

        if (defineInputStep.type === StepType.WorkflowDefineInput) {
            this.steps.forEach((step: Step) => {
                isConnectedToFileParam =
                    step.inputParameters.find((param: StepParameter) => {
                        return param instanceof StepOutputParameter && param.sourceFlowId === defineInputStep.flowId && param.sourceOutputKey === paramKey;
                    }) != null;
            });
        }

        return isConnectedToFileParam;
    }
}
